import { Field, FieldProps, FormikContextType, useFormikContext } from "formik";
import React, { Fragment, Suspense, useEffect } from "react";
import { useIMask } from "react-imask";
import Select from "react-select";
import { object } from "yup";

import { useGranite } from "@redux/modules/value/translations/translations.provider";

import { MicroCopy } from "@components/Form/FormFields/MicroCopy/microCopy.component";
import { FormFeedback } from "@components/General/Form/formFeedback.component";
import { FormGroup } from "@components/General/Form/formGroup.component";
import { FormText } from "@components/General/Form/formText.component";
import { Input } from "@components/General/Form/input.component";
import { InputGroup } from "@components/General/Form/inputGroup.component";
import { InputGroupAddon } from "@components/General/Form/inputGroupAddon.component";
import { Label } from "@components/General/Form/label.component";

import "./prefixedInput.scss";

type Props = {
    name: string;
    type?: "text" | "textarea" | "number" | "tel";
    hideLabel?: boolean;
    label: string;
    microCopy?: any;
    required?: boolean;
    optionalIndication?: boolean;
    prefixName: string;
    prefixOptions: Array<any>;
    inputMaskBasedOnPrefix?: any;
    autoComplete?: string | boolean;
    validationRule?: any;
    isLoading?: boolean;
    disabled?: boolean;
    renderOption: (option: any) => any;
    placeholder?: string;
    test?: string;
    onBlur?: () => void;
};

const PrefixedInputField = ({
    name,
    hideLabel,
    label,
    required,
    optionalIndication = true,
    microCopy,
    prefixName,
    type = "text",
    prefixOptions,
    inputMaskBasedOnPrefix,
    validationRule,
    disabled,
    renderOption,
    isLoading,
    autoComplete,
    placeholder,
    test,
    onBlur,
}: Props) => {
    const { t } = useGranite();
    const formik: FormikContextType<any> = useFormikContext();
    const inputMask =
        inputMaskBasedOnPrefix &&
        inputMaskBasedOnPrefix(formik.values[prefixName]);

    const options = {
        mask: inputMask?.mask,
        definitions: inputMask?.definitions,
        lazy: false,
        placeholderChar: inputMask?.placeHolderChar,
    };

    const { ref, maskRef } = useIMask(options, {
        onAccept: (value: any, mask: any) => {
            if ((value || value === "") && formik.values[name] !== value) {
                formik.setFieldValue(name, mask.value);
                mask.updateValue();
            }
        },
    });

    useEffect(() => {
        if (
            formik.initialValues[name] &&
            maskRef.current &&
            formik.initialValues[name] !== maskRef.current.value
        ) {
            formik.setFieldValue(name, formik.initialValues[name]);
        }
    }, [maskRef?.current]);

    useEffect(() => {
        const value = formik.values[name];

        if (value !== maskRef?.current?.value) {
            if (maskRef?.current) {
                maskRef.current.value = value || "";
            }
            maskRef?.current?.updateControl();
        }
    }, [formik.values[name]]);

    useEffect(() => {
        if (!options.mask && inputMask?.placeHolderChar) {
            formik.setFieldValue(
                name,
                formik.values[name]?.replaceAll(inputMask?.placeHolderChar, ""),
            );
        }
    }, [options.mask]);

    useEffect(() => {
        formik.validateField(name);
    }, [formik.values[prefixName]]);

    const customStyles = {
        container: (provided: any) => ({
            ...provided,
            width: 8 * 4 + 85,
        }),
        control: (provided: any) => ({
            ...provided,
            minHeight: "100%",
            borderBottomRightRadius: 0,
            borderTopRightRadius: 0,
        }),
        menu: (provided: any) => ({
            ...provided,
            width: "auto",
        }),
        indicatorSeparator: (provided: any) => ({
            ...provided,
            display: "none",
        }),
    };

    const onValidate = (value: any) => {
        if (!validationRule) {
            return null;
        }

        // Use this so the path can be passed
        const schema = object().shape({
            [name]: validationRule.label(label.toLowerCase()),
        });

        return new Promise(resolve => {
            schema
                .validate(
                    { ...formik.values, [name]: value },
                    {
                        context: { ...formik, prefixData: prefixOptions },
                        abortEarly: true,
                    },
                )
                .then(() => {
                    // The field is valid
                    resolve(null);
                })
                .catch(err => {
                    // The field is invalid and has errors
                    resolve(err.errors);
                });
        });
    };

    const onPaste = (e: any) => {
        e.preventDefault();
        if (e.clipboardData && e.clipboardData.getData) {
            formik.setFieldValue(
                name,
                e.clipboardData.getData("text/plain").replace(/\D/g, ""),
            );
        }
    };

    return (
        <Suspense fallback={<p>{t("common.loading")}</p>}>
            <FormGroup>
                {label && (
                    <Label for={name} hidden={hideLabel}>
                        {label}{" "}
                        {required
                            ? "*"
                            : optionalIndication && (
                                  <span className="font-italic small">
                                      {t("form.input.optional")}
                                  </span>
                              )}
                    </Label>
                )}
                {microCopy && (
                    <FormText
                        color="default"
                        dangerouslySetInnerHTML={{ __html: microCopy }}
                    />
                )}
                {!microCopy && <MicroCopy fieldId={name} />}
                <InputGroup>
                    <InputGroupAddon addonType="prepend">
                        <Field name={prefixName}>
                            {({ field, form, meta }: FieldProps) => {
                                return (
                                    <Select
                                        {...field}
                                        // @ts-ignore
                                        valid={meta.touched && !meta.error}
                                        invalid={meta.touched && !!meta.error}
                                        classNamePrefix="select-box"
                                        value={
                                            prefixOptions.find(
                                                option =>
                                                    option.value ===
                                                    field.value,
                                            ) || prefixOptions[0]
                                        }
                                        isSearchable={true}
                                        formatOptionLabel={renderOption}
                                        noOptionsMessage={() =>
                                            t("general.selectBox.no.options")
                                        }
                                        options={prefixOptions}
                                        styles={customStyles}
                                        onChange={(option: any) => {
                                            form.setFieldValue(
                                                field.name,
                                                option.value,
                                            );
                                        }}
                                        aria-label={t(
                                            "accessibility.phoneField.prefix.label",
                                        )}
                                        isDisabled={disabled}
                                    />
                                );
                            }}
                        </Field>
                    </InputGroupAddon>
                    <Field name={name} validate={onValidate}>
                        {({ field, meta }: FieldProps) => {
                            // We can't use onChange due to the workings of IMask vs Formik
                            const { onChange, ...rest } = field;
                            return (
                                <Fragment>
                                    <Input
                                        data-test={test}
                                        {...rest}
                                        onChange={
                                            inputMask?.mask
                                                ? () => {}
                                                : onChange
                                        }
                                        onFocus={() => {
                                            if (maskRef && maskRef.current) {
                                                maskRef.current.updateValue();
                                            }
                                        }}
                                        onPaste={e => onPaste(e)}
                                        onBlur={() => {
                                            formik.setFieldTouched(
                                                field.name,
                                                true,
                                            );
                                            onBlur && onBlur();
                                        }}
                                        id={name}
                                        innerRef={
                                            inputMask?.mask ? ref : undefined
                                        }
                                        className="prefixed-input__input"
                                        type={type}
                                        placeholder={
                                            inputMask?.mask ? "" : placeholder
                                        }
                                        autoComplete={autoComplete?.toString()}
                                        valid={
                                            meta.touched &&
                                            !meta.error &&
                                            !isLoading
                                        }
                                        invalid={
                                            meta.touched &&
                                            !!meta.error &&
                                            !isLoading
                                        }
                                        disabled={disabled}
                                    />

                                    {meta.touched &&
                                        !!meta.error &&
                                        !isLoading && (
                                            <FormFeedback>
                                                {meta.error}
                                            </FormFeedback>
                                        )}
                                </Fragment>
                            );
                        }}
                    </Field>
                </InputGroup>
            </FormGroup>
        </Suspense>
    );
};

export { PrefixedInputField };
